import React, { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchPermissions,
  fetchRolesPermissions,
  PermissionCategory,
} from "../services/RolePermissionService";
import {
  routes,
  flattenRoutes,
  IRoute,
  IPageRoute,
  FULL_ACCESS_ROLES,
} from "../routes";
import { getRealmRoles } from "../services/ServicesHelpers";

interface PermissionContextValue {
  filteredRoutes: IRoute[];
  isLoading: boolean;
}

const PermissionContext = createContext<PermissionContextValue>({
  filteredRoutes: [],
  isLoading: true,
});

const userRoles = getRealmRoles();
const isFullAccess = userRoles.some((r) => FULL_ACCESS_ROLES.includes(r));

function resolvePagePermissions(
  subpage: IPageRoute,
  permissionCategories: PermissionCategory[]
): string[] {
  if (subpage.permissions && subpage.permissions.length > 0) {
    return subpage.permissions;
  }
  if (subpage.pageKey) {
    for (const cat of permissionCategories) {
      const page = cat.pages.find((p) => p.page === subpage.pageKey);
      if (page) return page.permissions.map((permission) => permission.key);
    }
  }
  return [];
}

function filterRoutesByPermissions(
  allRoutes: IRoute[],
  userPermissions: Set<string>,
  permissionCategories: PermissionCategory[]
): IRoute[] {
  return allRoutes
    .map((route) => {
      if (!route.subpages || route.subpages.length === 0) return null;

      const visibleSubpages = route.subpages.filter((sub) => {
        const pagePerms = resolvePagePermissions(sub, permissionCategories);
        if (pagePerms.length === 0) return false;
        return pagePerms.some((p) => userPermissions.has(p));
      });

      if (visibleSubpages.length === 0) return null;

      return { ...route, subpages: visibleSubpages };
    })
    .filter(Boolean) as IRoute[];
}

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isLoggedIn = localStorage.getItem("access_token") !== null;

  const { data: rolesPermissionsData, isLoading: loadingRolesPerms } = useQuery(
    {
      queryKey: ["rolesPermissions"],
      queryFn: fetchRolesPermissions,
      enabled: isLoggedIn && !isFullAccess,
      staleTime: 5 * 60 * 1000,
    }
  );

  const { data: permissionCategories, isLoading: loadingCategories } = useQuery(
    {
      queryKey: ["permissionCategories"],
      queryFn: fetchPermissions,
      enabled: isLoggedIn && !isFullAccess,
      staleTime: 5 * 60 * 1000,
    }
  );

  const filteredRoutes = useMemo<IRoute[]>(() => {
    if (isFullAccess) {
      return routes;
    }

    if (!rolesPermissionsData || !permissionCategories) {
      return [];
    }

    const userPermissions = new Set<string>(
      userRoles.flatMap(
        (role) => (rolesPermissionsData.rolesPermissions[role] as string[]) ?? []
      )
    );

    return filterRoutesByPermissions(routes, userPermissions, permissionCategories);
  }, [rolesPermissionsData, permissionCategories]);

  const isLoading =
    !isFullAccess && isLoggedIn && (loadingRolesPerms || loadingCategories);

  const contextValue = useMemo(
    () => ({ filteredRoutes, isLoading }),
    [filteredRoutes, isLoading]
  );

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextValue =>
  useContext(PermissionContext);

/** Convenience: flat list of all page routes the user can access */
export const useFlatRoutes = (): IPageRoute[] => {
  const { filteredRoutes } = usePermissions();
  return useMemo(() => flattenRoutes(filteredRoutes), [filteredRoutes]);
};
